import type { DocumentFont, FontRef, ResolvedFont } from "@flobi/artwork-core";
import apiClient, { artworkCmd } from "./apiClient";

/** One family as the picker lists it. */
export interface FontFamily {
  readonly family: string;
  readonly category: string;
  readonly weights: number[];
  readonly italics: number[];
  readonly weightRange: string | null;
}

/**
 * The font catalogue, and the faces a family resolves to.
 *
 * The backend answers with urls; the document keeps them, and the canvas fetches them.
 * Nothing here holds font bytes — a font is a file somewhere, not a thing to be
 * installed, which is what lets the same file draw in a browser and in an export job.
 */
export async function listFonts(q: string, category?: string): Promise<{ items: FontFamily[]; total: number }> {
  const answer = await artworkCmd<{ items: FontFamily[]; total?: number }>(
    "artwork.fonts.list",
    { ...(q ? { q } : {}), ...(category ? { category } : {}), limit: 200 },
  );
  const items = answer.items ?? [];
  return { items, total: typeof answer.total === "number" ? answer.total : items.length };
}

export async function resolveFont(
  family: string,
  weights?: number[],
  italic?: boolean,
): Promise<DocumentFont> {
  return artworkCmd<DocumentFont>("artwork.fonts.resolve", {
    family,
    ...(weights?.length ? { weights } : {}),
    ...(italic ? { italic } : {}),
  });
}

/** One file in the Flobi Fonts library: a face of a family. */
export interface LibraryFont {
  readonly id: string;
  readonly family: string;
  readonly style: string;
  readonly weight: number;
  readonly italic: boolean;
  readonly format: string;
  readonly visibility: string;
  /** What Flobi Fonts reads it as: serif, sans, slab, mono, script, handwritten, display, symbol. */
  readonly classification: string | null;
  /** The brands and projects that use it, by id. Null when Fonts could not ask them. */
  readonly brands: readonly string[] | null;
  readonly projects: readonly string[] | null;
}

async function fontsCommand<T>(cmd: string, body: Record<string, unknown>): Promise<T> {
  const response = await apiClient.post(`/fonts/${cmd}`, body);
  if (response.data?.success === false) {
    // The status rides along so a reader can tell a refusal (4xx) from Fonts being down (5xx).
    const statusCode = typeof response.data.statusCode === "number" ? response.data.statusCode : undefined;
    throw Object.assign(Error(response.data.error || "Fonts is unavailable"), statusCode ? { statusCode } : {});
  }
  return response.data?.data ?? response.data;
}

/** The most the picker reads: a workspace's library is tens of families, and this is a ceiling, not a page size. */
const LIBRARY_PAGES = 5;

/**
 * Every face in the library this person can use, with the brands and projects each is
 * used by — which is what lets the picker put the file's brand and project first.
 */
export async function listLibrary(): Promise<LibraryFont[]> {
  const out: LibraryFont[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < LIBRARY_PAGES; page += 1) {
    const answer: { items?: Record<string, unknown>[]; nextCursor?: string | null } = await fontsCommand("fonts.library.list", { limit: 100, sort: "name", includeLinks: true, ...(cursor ? { cursor } : {}) });
    for (const row of answer.items ?? []) {
      if (typeof row.id !== "string" || typeof row.family !== "string") continue;
      const links = row.links as { brands?: { id: string }[] | null; projects?: { id: string }[] | null } | undefined;
      const ids = (list: { id: string }[] | null | undefined) => (Array.isArray(list) ? list.map((one) => one.id) : null);
      out.push({
        id: row.id,
        // The name Flobi Fonts shows, which a person may have given it: the file's own family
        // can be anything ("Frame Handoff" for the font named Flobi Chaos, Farah 27 Oct).
        family: typeof row.displayName === "string" && row.displayName.trim() ? row.displayName.trim() : row.family,
        style: typeof row.style === "string" ? row.style : "Regular",
        weight: Number(row.weight) || 400,
        italic: row.italic === true,
        format: String(row.format ?? ""),
        visibility: String(row.visibility ?? "private"),
        classification: (row.typography as { classification?: string } | null)?.classification ?? null,
        brands: ids(links?.brands),
        projects: ids(links?.projects),
      });
    }
    cursor = answer.nextCursor ?? null;
    if (!cursor) break;
  }
  return out;
}

/**
 * One library font's file, asked of Flobi Fonts by its id, or by its asset id for a
 * document saved before faces kept their id. Fonts answers with a signed url that works
 * for fifteen minutes, and only to someone who may still use the font — which is why the
 * canvas asks again each time it opens a file (`FontUrlResolver`, canvas/documentFonts.ts).
 */
export function askLibraryFont(ref: FontRef): Promise<ResolvedFont> {
  return fontsCommand<ResolvedFont>("fonts.artwork.resolve", "id" in ref ? { id: ref.id } : { assetId: ref.assetId });
}

/** fonts.artwork.resolve's whole answer: a document font, with the face's Fonts id and expiry. */
type LibraryFontAnswer = Pick<DocumentFont, "family" | "source" | "assetId"> &
  Omit<ResolvedFont, "faces"> & {
    readonly faces: readonly (ResolvedFont["faces"][number] & { readonly format?: DocumentFont["faces"][number]["format"] })[];
  };

/**
 * One library face as the document keeps it: its Fonts id, to ask for the file again by,
 * and a url that works until `expiresAt`.
 */
export async function resolveLibraryFont(id: string): Promise<DocumentFont> {
  const answer = await fontsCommand<LibraryFontAnswer>("fonts.artwork.resolve", { id });
  return {
    family: answer.family,
    source: answer.source ?? "upload",
    ...(answer.assetId ? { assetId: answer.assetId } : {}),
    faces: answer.faces.map((face) => ({
      url: face.url,
      format: face.format ?? "ttf",
      weight: face.weight ?? 400,
      italic: face.italic ?? false,
      fontId: face.fontId ?? answer.fontId ?? id,
      ...(answer.expiresAt ? { expiresAt: answer.expiresAt } : {}),
    })),
  };
}

/**
 * A library family as ONE font of the document: every face it has, under the family's own
 * name, so Bold and Italic are there to be chosen and an exported SVG names the family a
 * person would recognise. (A face used to be picked alone, under a name made unique with
 * its id — "Instrument Sans Regular c350b81a" — which gave a family of one weight.)
 */
export async function resolveLibraryFamily(family: string, faces: readonly LibraryFont[]): Promise<DocumentFont> {
  const usable = faces.filter((face) => face.format === "ttf" || face.format === "otf");
  if (usable.length === 0) throw Error("Artwork needs the TTF or OTF file of this font.");
  const resolved = await Promise.all(usable.map((face) => resolveLibraryFont(face.id)));
  const out: DocumentFont["faces"] = [];
  for (const font of resolved) {
    for (const face of font.faces) if (!out.some((had) => had.weight === face.weight && had.italic === face.italic)) out.push(face);
  }
  return { family: family.replace(/[^\p{L}\p{N} .'&+_-]/gu, " ").trim().slice(0, 128), source: "upload", ...(resolved[0]?.assetId ? { assetId: resolved[0].assetId } : {}), faces: out };
}
